import React, { ReactNode, useEffect } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useLocation } from 'react-router-dom';
import { MenuItemModel } from '@components/menu/types.ts';
import Menu from '@app/layout/Menu.tsx';
import MenuHeader from '@app/layout/MenuHeader.tsx';
import useHoverIntent from '@hooks/useHoverIntent/useHoverIntent.tsx';
import useScrollOverflow from '@hooks/useScrollOverflow/useScrollOverflow.tsx';
import useScrollMemory from '@hooks/useScrollMemory/useScrollMemory.tsx';
import ScrollEdge from '@components/RingilComponents/ScrollEdge.tsx';
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED, SidebarMode, useSidebarState } from '@app/layout/useSidebarState.tsx';

/** Transparent strip past the open panel that keeps it open, so the edge is not a cliff. */
const HOVER_BUFFER_PX = 48;

interface ComponentProps {
  state: ReturnType<typeof useSidebarState>;
  menu: MenuItemModel[];
  userMenu?: MenuItemModel[];
  /** Key under which the main menu's open group is remembered. */
  menuStorageKey: string;
  /** Key under which the bottom menu's open group is remembered. */
  userMenuStorageKey: string;
  /** Rendered between the header and the main menu; receives whether the sidebar is the icon rail. */
  headerSlot?: (args: { smallMenu: boolean }) => ReactNode;
  /** Rendered above the bottom menu; receives whether the sidebar is currently the icon rail. */
  bottomSlot?: (args: { smallMenu: boolean }) => ReactNode;
  /** Render the bottom menu inline with the main one instead of pinned to the footer. */
  userMenuInline?: boolean;
  /** Extra classes for the visible panel (e.g. the red runAs background). */
  panelClassName?: string;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Set false for shells that have no mobile drawer (admin), so narrow viewports keep the desktop menu. */
  enableMobile?: boolean;
}

/**
 * The application's left menu, shared by the customer, admin and public shells.
 *
 * Three sizes, from {@link useSidebarState}: `expanded` (270px) and `collapsed` (80px rail) are
 * explicit user choices, and `auto` picks between them from the viewport width.
 *
 * The outer <aside> and the visible panel have separate widths, and that is what makes push and
 * overlay one mechanism rather than two code paths. Expanded, both are 270px, so the menu occupies
 * the flow and pushes the content. Collapsed, the <aside> stays 80px no matter what while a hover
 * grows only the panel to 270px — the content underneath never moves, matching the design's
 * "menu isn't fixed — page width does NOT change. Menu is 'above'."
 */
const AppSidebar: React.FC<ComponentProps> = ({
  state,
  menu,
  userMenu,
  menuStorageKey,
  userMenuStorageKey,
  headerSlot,
  bottomSlot,
  userMenuInline = false,
  panelClassName,
  mobileOpen = false,
  onMobileClose,
  enableMobile = true,
}) => {
  const { size, mode, setMode } = state;
  const isMobile = enableMobile && state.isMobile;
  const location = useLocation();

  const { open: hoverOpen, close: closeHover, openNow: openHover, handlers } = useHoverIntent({ enabled: !isMobile && size === 'collapsed' });

  /**
   * Collapsing from the header control leaves the pointer inside the menu, and no mouseenter fires
   * for a pointer that never moved — so the panel would snap shut under the cursor. Opening the
   * hover overlay by hand keeps the panel on screen while the content reflows to the rail; it
   * closes as usual once the pointer leaves.
   */
  const selectMode = (next: SidebarMode) => {
    setMode(next);
    if (next !== 'expanded') openHover();
  };
  const { scrollRef, contentRef, overflowTop, overflowBottom } = useScrollOverflow();
  // Navigating remounts the shell, so the menu's scroll offset has to be carried over by hand.
  useScrollMemory(scrollRef, 'AppSidebar.menuScroll');

  // Picking an item is one of the two ways the design closes the menu (the other is leaving it).
  useEffect(() => {
    closeHover();
    onMobileClose?.();
  }, [location.pathname, location.search]);

  const expandedLook = isMobile ? true : size === 'expanded' || hoverOpen;
  const smallMenu = !expandedLook;
  const overlaying = !isMobile && size === 'collapsed' && hoverOpen;
  const asideWidth = size === 'expanded' ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  // Inline, the user menu sits near the top, so a fly-out anchored to its row's bottom would grow off
  // the top of the screen — it hangs down from the row instead.
  const inlineUserMenu = userMenu?.map(item => (item.mode === 'open-outside-bottom' ? { ...item, mode: 'open-outside-top' as const } : item));

  const body = (
    <>
      <div className={'flex flex-col grow min-h-0'}>
        {!isMobile && <MenuHeader smallMenu={smallMenu} mode={mode} onSelectMode={selectMode} />}
        {isMobile && <div className="pt-12 shrink-0" />}
        {headerSlot?.({ smallMenu })}
        {/* relative so the overflow indicators can sit on the scroll region's own edges. With the user
            menu inline it only takes its content's height, so that menu follows right below it. */}
        <div className={clsx('relative flex flex-col min-h-0', userMenuInline ? 'flex-initial' : 'grow')}>
          {/* Clipped horizontally so a 270px sub-item never pokes out of the rail mid-transition. */}
          <div ref={scrollRef} className={'flex flex-col grow w-full overflow-x-hidden overflow-y-auto'}>
            <div ref={contentRef} className={'flex flex-col'}>
              <Menu menu={menu} smallMenu={smallMenu} localStorageKey={menuStorageKey} onNavigate={closeHover} />
            </div>
          </div>
          <ScrollEdge edge="top" visible={overflowTop} />
          <ScrollEdge edge="bottom" visible={overflowBottom} />
        </div>
        {/* Outside the scroll region: its overflow would clip the user menu's 270px fly-outs. */}
        {userMenuInline && inlineUserMenu && (
          <div className={'shrink-0'}>
            <Menu menu={inlineUserMenu} smallMenu={smallMenu} localStorageKey={userMenuStorageKey} onNavigate={closeHover} />
          </div>
        )}
      </div>

      <div className={'mb-5 w-full shrink-0'}>
        {bottomSlot?.({ smallMenu })}
        {!userMenuInline && userMenu && <Menu menu={userMenu} smallMenu={smallMenu} localStorageKey={userMenuStorageKey} onNavigate={closeHover} />}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <aside
        className={twMerge(
          clsx('fixed inset-0 z-30 w-full flex-col justify-between bg-white shadow-lg transform transition-transform duration-300 motion-reduce:transition-none', {
            hidden: !mobileOpen,
            flex: mobileOpen,
            'translate-x-0': mobileOpen,
            '-translate-x-full': !mobileOpen,
          }),
          panelClassName,
        )}
      >
        {mobileOpen && (
          <div className="absolute top-4 right-4 p-2 text-xl cursor-pointer hover:bg-gray-100 rounded-full z-10" onClick={() => onMobileClose?.()}>
            <span className="pi pi-times"></span>
          </div>
        )}
        {body}
      </aside>
    );
  }

  return (
    <aside className={'relative h-full shrink-0 transition-[width] duration-200 ease-in-out motion-reduce:transition-none'} style={{ width: asideWidth }} {...handlers}>
      {/* Only present while the overlay is open, so it never blocks the page otherwise. It is a
          descendant of the <aside>, which is what keeps the pointer "inside" the sidebar for
          mouseleave purposes. It must come BEFORE the panel and stack below it: the fly-out
          sub-menus live inside the panel's stacking context and start at exactly this x, so a
          buffer painted on top would swallow their clicks and drop the group-hover that shows them. */}
      {overlaying && <div className={'absolute inset-y-0 z-30'} style={{ left: SIDEBAR_WIDTH_EXPANDED, width: HOVER_BUFFER_PX }} />}
      <div
        className={twMerge(
          clsx(
            'absolute inset-y-0 left-0 flex flex-col justify-between bg-base-secondary text-base-black transition-[width] duration-200 ease-in-out motion-reduce:transition-none',
            // Above page content, and above the hover buffer so fly-outs stay reachable.
            overlaying ? 'shadow-lg z-40' : 'z-10',
          ),
          panelClassName,
        )}
        style={{ width: expandedLook ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED }}
      >
        {body}
      </div>
    </aside>
  );
};

export default AppSidebar;
